/**
 * Span Component
 * Inline text component with design system tokens
 */

import React from "react";
import { Text as ChakraText } from "@chakra-ui/react";
import { TextColorToken, SurfaceColorToken, BorderColorToken } from "design-system/foundation/Color";
import { SpacingToken } from "design-system/foundation/Spacing";
import { SizeToken } from "design-system/foundation/Size";
import { RadiusToken } from "design-system/foundation/Radius";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { useResolveSpacing, useResolveSize, useResolveRadius } from "design-system/hooks/useResolveToken";

interface SpanProps {
  children: React.ReactNode;

  // Color props
  color?: TextColorToken;
  bg?: SurfaceColorToken;
  backgroundColor?: SurfaceColorToken;
  borderColor?: BorderColorToken;

  // Spacing props
  p?: SpacingToken;
  px?: SpacingToken;
  py?: SpacingToken;
  pt?: SpacingToken;
  pr?: SpacingToken;
  pb?: SpacingToken;
  pl?: SpacingToken;
  padding?: SpacingToken;
  m?: SpacingToken;
  mx?: SpacingToken;
  my?: SpacingToken;
  mt?: SpacingToken;
  mr?: SpacingToken;
  mb?: SpacingToken;
  ml?: SpacingToken;
  margin?: SpacingToken;

  // Size props
  w?: SizeToken;
  h?: SizeToken;
  width?: SizeToken;
  height?: SizeToken;
  minW?: SizeToken;
  minH?: SizeToken;
  maxW?: SizeToken;
  maxH?: SizeToken;

  // Border props
  borderRadius?: RadiusToken;
  borderWidth?: SpacingToken;

  // Text styling
  fontSize?: string;
  fontWeight?: string;
  fontFamily?: string;
  textAlign?: "left" | "center" | "right" | "justify";
  textDecoration?: string;
  textTransform?: "none" | "capitalize" | "uppercase" | "lowercase";
  lineHeight?: string | number;
  letterSpacing?: string;

  // Display and layout
  display?: "inline" | "inline-block" | "block";
  cursor?: string;
  userSelect?: "none" | "auto" | "text" | "all";
  whiteSpace?: "normal" | "nowrap" | "pre" | "pre-line" | "pre-wrap";
  overflow?: "visible" | "hidden" | "scroll" | "auto";
  textOverflow?: "clip" | "ellipsis";

  // Event handlers
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;

  // Accessibility
  role?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "data-testid"?: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

function SpanComponent({
  children,
  color,
  bg,
  backgroundColor,
  borderColor,
  p,
  px,
  py,
  pt,
  pr,
  pb,
  pl,
  padding,
  m,
  mx,
  my,
  mt,
  mr,
  mb,
  ml,
  margin,
  w,
  h,
  width,
  height,
  minW,
  minH,
  maxW,
  maxH,
  borderRadius,
  borderWidth,
  fontSize,
  fontWeight,
  fontFamily,
  textAlign,
  textDecoration,
  textTransform,
  lineHeight,
  letterSpacing,
  display = "inline",
  cursor,
  userSelect,
  whiteSpace,
  overflow,
  textOverflow,
  onClick,
  onMouseEnter,
  onMouseLeave,
  role,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "data-testid": testId,
  id,
  className,
  style,
  ...restProps
}: SpanProps) {
  // Resolve color tokens
  const resolvedColor = color ? useColorModeValue(color) : undefined;
  const resolvedBg = (bg || backgroundColor) ? useColorModeValue(bg || backgroundColor!) : undefined;
  const resolvedBorderColor = borderColor ? useColorModeValue(borderColor) : undefined;

  // Resolve spacing tokens
  const resolvedP = p ? useResolveSpacing(p).value : undefined;
  const resolvedPx = px ? useResolveSpacing(px).value : undefined;
  const resolvedPy = py ? useResolveSpacing(py).value : undefined;
  const resolvedPt = pt ? useResolveSpacing(pt).value : undefined;
  const resolvedPr = pr ? useResolveSpacing(pr).value : undefined;
  const resolvedPb = pb ? useResolveSpacing(pb).value : undefined;
  const resolvedPl = pl ? useResolveSpacing(pl).value : undefined;
  const resolvedPadding = padding ? useResolveSpacing(padding).value : undefined;

  const resolvedM = m ? useResolveSpacing(m).value : undefined;
  const resolvedMx = mx ? useResolveSpacing(mx).value : undefined;
  const resolvedMy = my ? useResolveSpacing(my).value : undefined;
  const resolvedMt = mt ? useResolveSpacing(mt).value : undefined;
  const resolvedMr = mr ? useResolveSpacing(mr).value : undefined;
  const resolvedMb = mb ? useResolveSpacing(mb).value : undefined;
  const resolvedMl = ml ? useResolveSpacing(ml).value : undefined;
  const resolvedMargin = margin ? useResolveSpacing(margin).value : undefined;

  // Resolve size tokens
  const resolvedW = w ? useResolveSize(w).value : undefined;
  const resolvedH = h ? useResolveSize(h).value : undefined;
  const resolvedWidth = width ? useResolveSize(width).value : undefined;
  const resolvedHeight = height ? useResolveSize(height).value : undefined;
  const resolvedMinW = minW ? useResolveSize(minW).value : undefined;
  const resolvedMinH = minH ? useResolveSize(minH).value : undefined;
  const resolvedMaxW = maxW ? useResolveSize(maxW).value : undefined;
  const resolvedMaxH = maxH ? useResolveSize(maxH).value : undefined;

  // Resolve other tokens
  const resolvedBorderRadius = borderRadius ? useResolveRadius(borderRadius).value : undefined;
  const resolvedBorderWidth = borderWidth ? useResolveSpacing(borderWidth).value : undefined;

  return (
    <ChakraText
      as="span"
      display={display}
      color={resolvedColor}
      bg={resolvedBg}
      backgroundColor={resolvedBg}
      borderColor={resolvedBorderColor}
      p={resolvedP || resolvedPadding}
      px={resolvedPx}
      py={resolvedPy}
      pt={resolvedPt}
      pr={resolvedPr}
      pb={resolvedPb}
      pl={resolvedPl}
      m={resolvedM || resolvedMargin}
      mx={resolvedMx}
      my={resolvedMy}
      mt={resolvedMt}
      mr={resolvedMr}
      mb={resolvedMb}
      ml={resolvedMl}
      w={resolvedW || resolvedWidth}
      h={resolvedH || resolvedHeight}
      width={resolvedW || resolvedWidth}
      height={resolvedH || resolvedHeight}
      minW={resolvedMinW}
      minH={resolvedMinH}
      maxW={resolvedMaxW}
      maxH={resolvedMaxH}
      borderRadius={resolvedBorderRadius}
      borderWidth={resolvedBorderWidth}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      textAlign={textAlign}
      textDecoration={textDecoration}
      textTransform={textTransform}
      lineHeight={lineHeight}
      letterSpacing={letterSpacing}
      cursor={cursor}
      userSelect={userSelect}
      whiteSpace={whiteSpace}
      overflow={overflow}
      textOverflow={textOverflow}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role={role}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      data-testid={testId}
      id={id}
      className={className}
      style={style}
      {...restProps}
    >
      {children}
    </ChakraText>
  );
}

// Public API
export const Span = SpanComponent;
